/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/
import React, { useState, useEffect } from 'react';
import type { CaseModel } from '../types';
import { UploadCloudIcon, CheckCircleIcon } from './icons';

interface WardrobePanelProps {
    onGarmentSelect: (garmentFile: File, garmentInfo: CaseModel) => void;
    activeGarmentIds: string[];
    isLoading: boolean;
    wardrobe: CaseModel[];
}

const urlToFile = (url: string, filename: string): Promise<File> => {
    return new Promise((resolve, reject) => {
        const image = new Image();
        image.setAttribute('crossOrigin', 'anonymous');
        image.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = image.naturalWidth;
            canvas.height = image.naturalHeight;
            const ctx = canvas.getContext('2d');
            if (!ctx) return reject(new Error('Could not get canvas context.'));
            ctx.drawImage(image, 0, 0);
            canvas.toBlob((blob) => {
                if (!blob) return reject(new Error('Canvas toBlob failed.'));
                const file = new File([blob], filename, { type: blob.type || 'image/png' });
                resolve(file);
            }, 'image/png');
        };
        image.onerror = (error) => reject(new Error(`Load failed: ${error}`));
        image.src = url;
    });
};

const WardrobePanel: React.FC<WardrobePanelProps> = ({ onGarmentSelect, activeGarmentIds, isLoading, wardrobe }) => {
    const [error, setError] = useState<string | null>(null);
    const [localLoading, setLocalLoading] = useState(false);
    const [clickedItemId, setClickedItemId] = useState<string | null>(null);

    // Reset local loading when parent isLoading changes
    useEffect(() => {
        if (!isLoading) {
            setLocalLoading(false);
            setClickedItemId(null);
        }
    }, [isLoading]);

    const handleCaseClick = async (item: CaseModel) => {
        // Prevent multiple rapid clicks
        if (isLoading || localLoading) return;

        setError(null);
        setLocalLoading(true);
        setClickedItemId(item.id); // Track which item was clicked for visual feedback
        try {
            const file = await urlToFile(item.url, item.name);
            onGarmentSelect(file, item);
        } catch (err) {
            console.error('Error selecting case:', err);
            setError('Không thể tải mẫu ốp lưng. Vui lòng thử lại.');
        } finally {
            // Only reset if parent hasn't taken over with a global isLoading
            // If isLoading is true, parent will reset these via useEffect
            setTimeout(() => {
                setLocalLoading(false);
                setClickedItemId(null);
            }, 500);
        }
    };

    return (
        <div className="pt-2">
            <h2 className="text-base font-semibold text-slate-800 mb-4 flex items-center justify-between">
                <span>Chọn Dòng Máy</span>
                <span className="text-xs font-normal text-slate-500 bg-slate-100 px-2 py-1 rounded-full">{wardrobe.length} mẫu</span>
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 md:gap-4">
                {wardrobe.map((item) => {
                    const isActive = activeGarmentIds.includes(item.id);
                    const isClicked = clickedItemId === item.id;
                    return (
                        <button
                            key={item.id}
                            onClick={() => handleCaseClick(item)}
                            disabled={isLoading || localLoading}
                            className={`relative flex flex-col items-center border rounded-xl overflow-hidden transition-all duration-200 text-left min-h-[140px] ${isClicked
                                    ? 'ring-4 ring-indigo-500 border-indigo-500 shadow-xl scale-95 bg-indigo-50'
                                    : isActive
                                        ? 'ring-2 ring-slate-700 border-slate-700 shadow-md'
                                        : 'border-slate-200 bg-white active:scale-95'
                                } ${(isLoading || localLoading) && !isClicked ? 'opacity-30 cursor-not-allowed' : ''}`}
                        >
                            <div className="w-full aspect-[4/5] bg-slate-50 p-3 flex items-center justify-center relative">
                                <img src={item.url} alt={item.name} className="w-full h-full object-contain" />
                                {isClicked && (
                                    <div className="absolute inset-0 bg-indigo-500/10 flex items-center justify-center">
                                        <div className="w-8 h-8 border-3 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
                                    </div>
                                )}
                            </div>
                            <div className="w-full p-2.5 bg-white border-t border-slate-100">
                                <p className={`text-xs font-medium truncate ${isClicked ? 'text-indigo-700' : 'text-slate-800'}`}>
                                    {isClicked ? 'Đang xử lý...' : item.name}
                                </p>
                                <p className="text-[10px] text-slate-500 mt-0.5">
                                    {isClicked ? 'Vui lòng đợi' : 'Mặc định'}
                                </p>
                            </div>
                            {isActive && !isClicked && (
                                <div className="absolute top-2 right-2 bg-white rounded-full shadow-sm p-1">
                                    <CheckCircleIcon className="w-4 h-4 text-slate-700" />
                                </div>
                            )}
                        </button>
                    );
                })}
            </div>
            {error && <p className="text-red-600 text-sm mt-4 p-3 bg-red-50 rounded-lg border border-red-200">{error}</p>}
        </div>
    );
};

export default WardrobePanel;